import { getRouteApi } from '@tanstack/react-router'
import { z } from 'zod'

import { ActionDetail } from '@/features/actions/ActionDetail'
import { RuleDetail } from '@/features/actions/RuleDetail'

/**
 * `/actions/$id?kind=action|rule&edit=1` — one action (default) or one world rule (docs/SKEL-STORY.md
 * R7, §11.7). Actions and rules live in separate id spaces, so the kind travels in the search;
 * `edit=1` opens the manual editor.
 */
const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined)

export const ACTION_KINDS = ['action', 'rule'] as const
/** `1`, `true` (or their strings) → `1`; anything else is dropped. */
const flag = z.union([z.literal(1), z.literal(true), z.literal('1'), z.literal('true')]).transform(() => 1 as const)
export const actionDetailSearchSchema = z.object({ kind: lenient(z.enum(ACTION_KINDS)), edit: lenient(flag) })
export type ActionDetailSearch = z.infer<typeof actionDetailSearchSchema>

export function validateActionDetailSearch(input: Record<string, unknown>): ActionDetailSearch {
  return actionDetailSearchSchema.parse(input)
}

export const ACTION_DETAIL_ROUTE_ID = '/actions/$id' as const
const route = getRouteApi(ACTION_DETAIL_ROUTE_ID)

export function ActionDetailRoutePage() {
  const { id } = route.useParams()
  const { kind, edit } = route.useSearch()
  // Keyed by id: another action remounts the page (fresh editor, fresh preview).
  return kind === 'rule' ? <RuleDetail key={`rule:${id}`} id={id} editing={edit === 1} /> : <ActionDetail key={`action:${id}`} id={id} editing={edit === 1} />
}
